import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from "react-hook-form";

import { format, parseISO } from 'date-fns';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";


import MainTitle from '../common/MainTitle';
import Modal from '../common/Modal';
import Helper from '../common/Helper';
import Strings from '../common/Strings';
import Toasts from '../common/Toasts';

import './BackupInstructions.css';

import BackupInstructionsStrings from './BackupInstructionsStrings';
import BackupInstructionsService from './BackupInstructionsService';
import DirectoryEditor from './DirectoryEditor';
import MediaStorageEditor from './MediaStorageEditor';


function uuidv4() {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
        (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
    );
}

const BackupInstructionEditor = ({ data, onUpdated, cloneData }) => {

    const [message, setMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();
    const [databases, setDatabases] = useState('');
    const [directories, setDirectories] = useState('');

    const validationSchema = yup.object().shape({
        name: yup.string().required(),
        description: yup.string(),
        enabled: yup.bool(),
        rotation: yup.string().required(),
        archive_prefix: yup.string().test(
            'archive-prefix-optional',
            'Префикс должен быть 1-30 символов: только a-z, 0-9 и дефисы, без пробелов и подчёркиваний.',
            (value) => {
                if (value === undefined || value === null) return true;
                const v = String(value).trim();
                if (!v) return true;
                return /^[a-z0-9][a-z0-9-]{0,29}$/.test(v);
            }
        ),

        s3: yup.object({
            host: yup.string().required(),
            access_key: yup.string().required(),
            secret_key: yup.string().required(),
            bucket_name: yup.string().required(),
            use_ssl: yup.bool()
        }),
        use_tgz: yup.bool(),
    });

    const { register, handleSubmit, setValue, reset, watch, control, unregister, formState: { errors } } = useForm({
        shouldUnregister: true,
        resolver: yupResolver(validationSchema),

        defaultValues: {
            name: "",
            description: "",
            enabled: false,
            s3: {
                host: "",
                access_key: "",
                secret_key: "",
                bucket_name: "",
                use_ssl: true
            },
            databases: [],
            directories: [],
            media_storages: [],
            rotation: "5d2w10m",
            use_tgz: false,
            archive_prefix: ""
        }
    });


    const { fields: dbFields,
        remove: removeDB,
        append: appendDB } = useFieldArray({ control, name: "databases" });

    const { fields: dirFields,
        remove: removeDir,
        append: appendDir } = useFieldArray({ control, name: "directories" });

    const { fields: mediaStorageFields,
        remove: removeMediaStorage,
        append: appendMediaStorage } = useFieldArray({ control, name: "media_storages" });



    useEffect(() => {

        if (data) {
            const normalized = { ...data };
            if (normalized.media_storages && Array.isArray(normalized.media_storages)) {
                normalized.media_storages = normalized.media_storages.map(ms => ({
                    ...ms,
                    excluded_directories: Array.isArray(ms.excluded_directories) ? ms.excluded_directories : []
                }));
            }
            Object.entries(normalized).forEach((dataItem) => {
                setValue(dataItem[0], dataItem[1]);
            });
        } else if (cloneData) {
            const normalized = { ...cloneData };
            if (normalized.media_storages && Array.isArray(normalized.media_storages)) {
                normalized.media_storages = normalized.media_storages.map(ms => ({
                    ...ms,
                    excluded_directories: Array.isArray(ms.excluded_directories) ? ms.excluded_directories : []
                }));
            }
            Object.entries(normalized).forEach((dataItem) => {
                if (['name', 'creation_date', 'description', 'key'].indexOf(dataItem[0]) === -1) {
                    setValue(dataItem[0], dataItem[1]);
                }
            });
        }

    }, []);


    // [
    //     {
    //         "name": "swcorebot",
    //         "description": "swcorebot",
    //         "creation_date": "2017-07-21T17:32:28Z",
    //         "key": "0lMCBQC12Zu3fCFk6shzVkbmI5Pjho",
    //         "enabled": true,
    //         "databases": [
    //             {
    //                 "db_type": "mongo",
    //                 "host": "hostname",
    //                 "port": 21017,
    //                 "database": "swcorebot",
    //                 "username": "db_user",
    //                 "password": "swcorebot",
    //                 "archive_filename_part": "swcorebot"
    //             }
    //         ],
    //         "directories": [
    //             {
    //                 "path": "/var/www/somebot/",
    //                 "excluded_directories": [
    //                     "*__pycache__"
    //                 ],
    //                 "archive_filename_part": "swcorebot"
    //             }
    //         ],
    //         "s3": {
    //             "host": "swcorebot.ru",
    //             "access_key": "SQblJfGXw42azYRcLu898JyMf5ie8H3t",
    //             "secret_key": "NGnC16Fg/$",
    //             "bucket_name": "swcorebot",
    //             "use_ssl": true
    //         },
    //         "rotation": "3d2w10m"
    //     }
    // ]

    const handleUpdate = (formData) => {

        setMessage("");
        setSuccessMessage("");
        const func = data ? BackupInstructionsService.update : BackupInstructionsService.create;

        func(formData).then((response) => {
            setSuccessMessage("Запись успешно " + (data ? "обновлена" : "создана"));
            setInterval(() => {
                setSuccessMessage("");
            }, 5000);
            onUpdated();
            if (!data || cloneData) {
                navigate("/backup_instructions/");
                reset();
            }
        },
            (error) => {
                setMessage(Helper.errorToString(error));
            });
    };

    const addDatabase = (e) => {
        e.preventDefault();
        appendDB({
            "db_type": "mongo",
            "host": "hostname",
            "port": 21017,
            "database": "",
            "username": "",
            "password": "",
            "archive_filename_part": ""
        });
    };

    const deleteDatabase = (e, index) => {
        e.preventDefault();
        removeDB(index);
    };

    const addDirectory = (e) => {
        e.preventDefault();
        appendDir({
            "path": "/var/www/",
            "excluded_directories": [],
            "archive_filename_part": ""
        });
    };

    const deleteDirectory = (e, index) => {
        e.preventDefault();
        removeDir(index);
    };

    const addMediaStorage = (e) => {
        e.preventDefault();
        appendMediaStorage({
            path: "/var/media/",
            s3_prefix: "",
            excluded_directories: []
        });
    };

    const deleteMediaStorage = (e, index) => {
        e.preventDefault();
        removeMediaStorage(index);
    };

    const directoriesRender = !dirFields ? "Директорий пока нет" : dirFields.map((dir, index) => {
        return <DirectoryEditor key={dir.id} control={control} directory={dir} dirIndex={index} unregister={unregister} register={register} deleteDirectory={deleteDirectory} />
    });

    const mediaStoragesRender = !mediaStorageFields ? "Нет" : mediaStorageFields.map((ms, index) => {
        return <MediaStorageEditor key={ms.id} mediaStorage={ms} msIndex={index} control={control} unregister={unregister} register={register} deleteMediaStorage={deleteMediaStorage} />
    });

    const databasesRender = !dbFields ? "Баз данных пока нет" : dbFields.map((db, index) => {
        const namePrefix = "databases[" + index + "]";
        return <div className='dbitem' key={db.id}>
            <button onClick={(e) => deleteDatabase(e, index)} className='btn trash-btn'><em className="fa fa-trash-o"></em></button>
            <div className='row'>
                <div className='col-lg-6'>
                    <label>Тип базы</label>
                    <select
                        {...register(namePrefix + '[db_type]')}
                        className="form-control"
                    >
                        <option>mongo</option>
                        <option>mysql</option>
                        <option>postgres</option>
                    </select>
                </div>
                <div className='col-lg-6'>
                    <label>Хост</label>
                    <input role="presentation"
                        {...register(namePrefix + '[host]')}
                        className="form-control"
                    />
                </div>
                <div className='col-lg-6'>
                    <label>Порт</label>
                    <input role="presentation"
                        {...register(namePrefix + '[port]')}
                        className="form-control"
                    />
                </div>
                <div className='col-lg-6'>
                    <label>База</label>
                    <input role="presentation"
                        {...register(namePrefix + '[database]')}
                        className="form-control"
                    />
                </div>
                <div className='col-lg-6'>
                    <label>Юзер</label>
                    <input role="presentation"
                        {...register(namePrefix + '[username]')}
                        className="form-control"
                    />
                </div>
                <div className='col-lg-6'>
                    <label>Пароль</label>
                    <input role="presentation"
                        {...register(namePrefix + '[password]')}
                        className="form-control"
                    />
                </div>
                <div className='col-lg-6'>
                    <label>Постфикс архива <span>(необязательно)</span></label>
                    <input role="presentation"
                        {...register(namePrefix + '[archive_filename_part]')}
                        className="form-control"
                    />
                </div>
            </div>
        </div>


        //return <Question key={question.id} question={question} register={register} unregister={unregister} index={index} setValue={setValue} onDelete={(e) => deleteQuestion(e, index)} />
    })

    return (<div>
        {data && <ul className="no-bullets">
            <li>Ключ: {data.key}</li>
            <li>Дата создания: {format(parseISO(data.creation_date), "dd.MM.yyyy")}</li>
        </ul>}

        <form onSubmit={handleSubmit(handleUpdate)} autoComplete="off">
            <div className='row'>
                <div className=' col-lg-6'>
                    <div className="form-group">

                        <label>Название</label>
                        <input role="presentation"
                            {...register('name')}
                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        />
                        <div className="form-error">{errors.name?.message}</div>


                    </div>
                    <div className="form-group">
                        <label>Ротация</label>
                        <input role="presentation"
                            {...register('rotation')}
                            className={`form-control ${errors.rotation ? 'is-invalid' : ''}`}
                        />
                        <div className="form-error">{errors.rotation?.message}</div>
                    </div>
                    <div className="form-group">
                        <label>Префикс архива <span>(необязательно)</span></label>
                        <input
                            role="presentation"
                            {...register('archive_prefix')}
                            className={`form-control ${errors.archive_prefix ? 'is-invalid' : ''}`}
                            placeholder="example-prefix"
                        />
                        <div className="form-error">{errors.archive_prefix?.message}</div>
                    </div>
                </div>

                <div className="form-group col-lg-6">
                    <label>Описание <span>(необязательно)</span></label>
                    <textarea rows="4" role="presentation"
                        {...register('description')}
                        className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                    ></textarea>
                    <div className="form-error">{errors.description?.message}</div>
                </div>

            </div>
            <div className="dbitem row">
                <div className='form-group col-lg-6'>
                    <label>S3 Хост</label>
                    <input role="presentation"
                        {...register('[s3][host]')}
                        className={`form-control ${errors.s3 && errors.s3.host ? 'is-invalid' : ''}`}
                    />
                </div>
                <div className='form-group col-lg-6'>
                    <label>S3 Bucket Name</label>
                    <input role="presentation"
                        {...register('[s3][bucket_name]')}
                        className={`form-control ${errors.s3 && errors.s3.bucket_name ? 'is-invalid' : ''}`}
                    />
                </div>
                <div className='form-group col-lg-6'>
                    <label>S3 Ключ доступа</label>
                    <input role="presentation"
                        {...register('[s3][access_key]')}
                        className={`form-control ${errors.s3 && errors.s3.access_key ? 'is-invalid' : ''}`}
                    />
                </div>
                <div className='form-group col-lg-6'>
                    <label>S3 Секретный ключ</label>
                    <input role="presentation"
                        {...register('[s3][secret_key]')}
                        className={`form-control ${errors.s3 && errors.s3.secret_key ? 'is-invalid' : ''}`}
                    />
                </div>

                <div className='form-group col-lg-6'>
                    <label className="checkbox-s3-ssl">
                        <input role="presentation" type="checkbox"
                            {...register('[s3][use_ssl]')}
                            className="checkbox-s3-ssl__input"
                        />
                        <span className="checkbox-s3-ssl__box" aria-hidden="true" />
                        <span className="checkbox-s3-ssl__label">S3 Использовать SSL</span>
                    </label>
                </div>

                <div className='form-group col-lg-6'>
                    <label className="checkbox-s3-ssl">
                        <input
                            role="presentation"
                            type="checkbox"
                            {...register('use_tgz')}
                            className="checkbox-s3-ssl__input"
                        />
                        <span className="checkbox-s3-ssl__box" aria-hidden="true" />
                        <span className="checkbox-s3-ssl__label">Использовать tgz вместо 7z</span>
                    </label>
                </div>

            </div>
            <div className='mt-4'>
                <div className='d-flex'><h4>Базы данных</h4><button onClick={addDatabase} className='btn btn-secondary ms-3'><em className='fa fa-plus'></em></button></div>
                <div>
                    {databasesRender}
                </div>
            </div>

            <div className='mt-4'>
                <div className='d-flex'><h4>Директории</h4><button type="button" onClick={addDirectory} className='btn btn-secondary ms-3'><em className='fa fa-plus'></em></button></div>
                <div>
                    {directoriesRender}
                </div>
            </div>

            <div className='mt-4'>
                <div className='d-flex'><h4>Media storage (синхронизация)</h4><button type="button" onClick={addMediaStorage} className='btn btn-secondary ms-3'><em className='fa fa-plus'></em></button></div>
                <div>
                    {mediaStoragesRender}
                </div>
            </div>

            <div className="mt-2 d-flex align-items-center justify-content-between">
                <button type="submit" className="btn btn-primary">
                    {data ? "Обновить" : "Создать"}
                </button>
            </div>
        </form >
        {message && (<div className="alert alert-danger mt-4">
            {message}
        </div>)}
        {
            successMessage && (<div className="alert alert-success mt-4">
                {successMessage}
            </div>)
        }
    </div >)
}

export default BackupInstructionEditor